import { Box, SxProps, TextField, Theme } from '@mui/material';
import React, { useCallback, useState } from 'react';

import { MobileLabel } from '../../Typography';
import { runValidationRules } from '../validation';
import type { MobileTextAreaProps } from './types';

const inputSx: SxProps<Theme> = {
  borderRadius: 2,
  fontSize: '0.9375rem',
  fontWeight: 500,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'divider',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderWidth: 1,
  },
};

const MobileTextArea: React.FC<MobileTextAreaProps> = ({
  value,
  onChange,
  label,
  placeholder,
  rows = 3,
  disabled = false,
  required = false,
  error = false,
  helperText,
  validationRules,
  onBlur,
  fullWidth = true,
  sx,
}) => {
  const [touched, setTouched] = useState(false);
  const [internalError, setInternalError] = useState<string | null>(null);

  const validate = useCallback(
    (val: string) => runValidationRules(val, validationRules),
    [validationRules],
  );

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const next = e.target.value;
    onChange(next);
    if (validationRules?.length && touched) {
      setInternalError(validate(next));
    }
  };

  const handleBlur = () => {
    setTouched(true);
    if (validationRules?.length) {
      setInternalError(validate(value));
    }
    onBlur?.();
  };

  const showError = error || (touched && internalError != null);
  const displayHelperText = helperText ?? internalError ?? '';

  return (
    <Box sx={{ width: fullWidth ? '100%' : undefined, ...sx }}>
      {label != null && label !== '' && (
        <Box sx={{ mb: 0.75 }}>
          <MobileLabel component="span" sx={{ color: 'text.primary' }}>
            {label}
            {required && ' *'}
          </MobileLabel>
        </Box>
      )}
      <TextField
        fullWidth={fullWidth}
        multiline
        rows={rows}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        error={showError}
        variant="outlined"
        sx={inputSx}
        inputProps={{
          'aria-label': label,
        }}
      />
      {showError && displayHelperText && (
        <MobileLabel
          component="span"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'error.main',
            mt: 0.5,
            display: 'block',
          }}
        >
          {displayHelperText}
        </MobileLabel>
      )}
    </Box>
  );
};

export default MobileTextArea;
export type { MobileTextAreaProps, ValidationRule } from './types';
